import { useMemo, useState } from "react";
import type { Movimiento } from "@/lib/api";
import {
  OPCIONES_TIPO_MOVIMIENTO,
  PAGINA_MOVIMIENTOS,
  filtrarMovimientos,
  filtrosMovimientosIniciales,
  navegacionMes,
  opcionesMesMovimientos,
  textoCargarMas,
  type FiltrosMovimientos,
} from "@/lib/cuentas";

/**
 * Los movimientos de una cuenta corriente con filtros (Mati, 2026-11-02: "que por defecto muestre el último mes para que
 * no se convierta en una lista sin fin"): mes con flechas ‹ › entre meses calendario, tipo, y de a 20 filas con "Cargar más".
 */
export function useMovimientosCuenta(movimientos: Movimiento[]) {
  const [filtros, setFiltros] = useState<FiltrosMovimientos>(() => filtrosMovimientosIniciales(movimientos));
  const [visibles, setVisibles] = useState(PAGINA_MOVIMIENTOS);
  const filtrados = useMemo(() => filtrarMovimientos(movimientos, filtros), [movimientos, filtros]);
  const navegacion = navegacionMes(movimientos, filtros.mes);

  const cambiar = (cambio: Partial<FiltrosMovimientos>) => {
    setFiltros((f) => ({ ...f, ...cambio }));
    setVisibles(PAGINA_MOVIMIENTOS);
  };

  return {
    filtros,
    opcionesMes: opcionesMesMovimientos(movimientos),
    opcionesTipo: OPCIONES_TIPO_MOVIMIENTO,
    cambiarMes: (mes: string) => cambiar({ mes }),
    cambiarTipo: (tipo: string) => cambiar({ tipo: tipo as FiltrosMovimientos["tipo"] }),
    mesAnterior: navegacion.anterior ? () => cambiar({ mes: navegacion.anterior! }) : null,
    mesSiguiente: navegacion.siguiente ? () => cambiar({ mes: navegacion.siguiente! }) : null,
    filas: filtrados.slice(0, visibles).map((m, n) => ({ ...m, n })),
    textoCargarMas: textoCargarMas(filtrados.length, visibles),
    cargarMas: () => setVisibles((v) => v + PAGINA_MOVIMIENTOS),
    hayMovimientos: movimientos.length > 0,
  };
}
